import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle } from 'lucide-react';

const POLL_INTERVAL_MS = 15000; // check for new notifications every 15s
const API = process.env.REACT_APP_BACKEND_API_URL;

// tasksRoute: where clicking a notification should navigate to
// ("/team-lead/tasks" or "/recruiter/tasks")
const NotificationBell = ({ userName, tasksRoute }) => {
    const { isMobile } = useResponsive();
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const wrapperRef = useRef(null);

    const fetchNotifications = useCallback(() => {
        if (!userName) return;
        fetch(`${API}/api/notifications?user=${encodeURIComponent(userName)}`)
            .then(res => res.json())
            .then(data => setNotifications(data))
            .catch(err => console.error('Failed to fetch notifications:', err));
    }, [userName]);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    // Close the dropdown when clicking outside it
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const unreadCount = notifications.filter(n => !n.is_read).length;

    const handleNotificationClick = (notification) => {
        if (!notification.is_read) {
            fetch(`${API}/api/notifications/${notification.task_id}/${notification.id}/read`, { method: 'PATCH' })
                .then(() => fetchNotifications())
                .catch(err => console.error('Failed to mark notification as read:', err));
        }
        setIsOpen(false);
        navigate(tasksRoute);
    };

    const timeAgo = (dateStr) => {
        const diffMs = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        return `${Math.floor(hrs / 24)}d ago`;
    };

    return (
        <div ref={wrapperRef} style={{ position: 'relative' }}>
            <button
                onClick={() => setIsOpen(prev => !prev)}
                style={{
                    position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '36px', height: '36px',
                    cursor: 'pointer', color: '#334155'
                }}
                title="Notifications"
            >
                <Bell size={16} />
                {unreadCount > 0 && (
                    <span style={{
                        position: 'absolute', top: '-4px', right: '-4px', background: '#dc2626', color: '#fff',
                        borderRadius: '10px', fontSize: '10px', fontWeight: '700', minWidth: '16px', height: '16px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px'
                    }}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div style={{
                    position: 'absolute', top: '44px', right: 0, width: isMobile ? '85vw' : '340px', maxWidth: '340px', maxHeight: '400px', overflowY: 'auto',
                    background: '#fff', borderRadius: '10px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    border: '1px solid #e2e8f0', zIndex: 2000
                }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', fontWeight: '700', fontSize: '13px', color: '#1f2937' }}>
                        Notifications
                    </div>

                    {notifications.length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center', fontSize: '13px', color: '#94a3b8' }}>
                            No notifications yet.
                        </div>
                    ) : (
                        notifications.map(n => (
                            <div
                                key={n.id}
                                onClick={() => handleNotificationClick(n)}
                                style={{
                                    padding: '12px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                                    background: n.is_read ? '#fff' : '#eff6ff',
                                    display: 'flex', gap: '10px', alignItems: 'flex-start'
                                }}
                            >
                                <div style={{ marginTop: '2px', color: n.is_read ? '#cbd5e1' : '#2563eb' }}>
                                    {n.is_read ? <CheckCircle size={14} /> : <Bell size={14} />}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '12px', color: '#1f2937', fontWeight: n.is_read ? '400' : '600', lineHeight: '1.4' }}>
                                        {n.message}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>
                                        {timeAgo(n.created_at)}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;